import * as React from "react";
import { RouteComponentProps } from "react-router";
import { API_UserNotifications } from "../../../src/apidefs";
import { User } from "../models/users";
import { useUser, setUser, getUser } from "../state";
import { API, api } from "../api";
import { RLink } from "../DesignSystem/RLink";
import { Search } from "./Search";
import { GoldCoin } from "./GoldCoin";
import { SilverCoin } from "./SilverCoin";
import { TestIds } from "../testIds";
import {
  SunIcon,
  MoonIcon,
  PencilIcon,
  UserIcon,
  StarIcon,
  WrenchScrewdriverIcon,
} from "@heroicons/react/24/solid";
import { MiyoEasterEggMenu } from "./MiyoEasterEggMenu";
import { useIsImpersonating } from "../Layout/ImpersonationBanner";

type Theme = "light" | "dark";
const THEME_STORAGE_KEY = "chips-theme";

interface NavToolbarInnerProps extends RouteComponentProps<{}> {
  notifications: API_UserNotifications | null;
  user: User | "logged-out";
  isImpersonating: boolean;
}

interface NavToolbarState {
  unreadNotifications: number | null;
  unreadConvosCount: number;
  theme: Theme;
  userDropdownOpen: boolean;
}

export let NavToolbarInstance: NavToolbarInner;

class NavToolbarInner extends React.Component<
  NavToolbarInnerProps,
  NavToolbarState
> {
  toolbar?: HTMLDivElement;

  state: NavToolbarState = {
    unreadConvosCount: 0,
    unreadNotifications: 0,
    theme: "dark",
    userDropdownOpen: false,
  };

  dropdownRef = React.createRef<HTMLDivElement>();

  componentDidMount() {
    NavToolbarInstance = this;

    this.updateNotifications(this.props);
    this.componentDidMountTheme();
    document.addEventListener("mousedown", this.handleClickOutside);
    this.showUnreadMessageCount();
  }

  componentWillUnmount() {
    document.removeEventListener("mousedown", this.handleClickOutside);
  }

  handleClickOutside = (event: MouseEvent): void => {
    if (
      this.dropdownRef.current &&
      !this.dropdownRef.current.contains(event.target as Node)
    ) {
      this.closeUserDropdown();
    }
  };

  componentWillReceiveProps(newProps: NavToolbarInnerProps): void {
    this.updateNotifications(newProps);
  }

  updateNotifications(props: NavToolbarInnerProps): void {
    this.setState({
      unreadNotifications: props.notifications
        ? props.notifications.unread_count
        : null,
    });
  }

  clearNotificationCount() {
    this.setState({
      unreadNotifications: 0,
    });
  }

  async showUnreadMessageCount(): Promise<void> {
    const messages = await API.getMessages();
    let unreadConvosCount: number;
    unreadConvosCount = messages.unread_count;
    this.setState({ unreadConvosCount });
  }

  giveCoins(amount: number): void {
    const userState = getUser();
    if (userState.type === "logged-in") {
      userState.user.coins += amount;
      setUser({ type: "logged-in", user: new User({ ...userState.user }) });
    }
  }

  componentDidMountTheme(): void {
    const theme = this.getInitialTheme();
    this.applyTheme(theme);
  }

  getInitialTheme(): Theme {
    if (typeof window === "undefined") {
      return "dark";
    }

    const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
    if (stored === "light" || stored === "dark") {
      return stored;
    }

    return window.matchMedia &&
      window.matchMedia("(prefers-color-scheme: light)").matches
      ? "light"
      : "dark";
  }

  applyTheme(theme: Theme) {
    if (typeof document !== "undefined") {
      const root = document.documentElement;
      root.classList.remove("light", "dark");
      root.classList.add(theme);
    }

    if (typeof window !== "undefined") {
      window.localStorage.setItem(THEME_STORAGE_KEY, theme);
    }

    this.setState({ theme });
  }

  toggleTheme = (): void => {
    const nextTheme = this.state.theme === "dark" ? "light" : "dark";
    this.applyTheme(nextTheme);
  };

  async logout(): Promise<void> {
    await api.get("/api/logout");
  }

  toggleUserDropdown = (): void => {
    this.setState((prev) => ({ userDropdownOpen: !prev.userDropdownOpen }));
  };

  closeUserDropdown = (): void => {
    this.setState({ userDropdownOpen: false });
  };

  render(): JSX.Element {
    let rightSide: JSX.Element;
    const user = this.props.user;
    const isLoggedIn = user !== "logged-out";
    const hasNotifications =
      isLoggedIn &&
      this.state.unreadNotifications &&
      this.state.unreadNotifications > 0;

    if (isLoggedIn) {
      rightSide = (
        <div className="flex items-center gap-2 text-sm text-slate-900 dark:text-white md:gap-3">
          {/* Notifications */}
          <RLink
            white
            noUnderline
            className="inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm transition hover:bg-slate-100 dark:hover:bg-white/10"
            onClick={() => this.clearNotificationCount()}
            to="/notifications"
          >
            <i
              className={`fa fa-bell-o ${hasNotifications ? "text-chips-gold" : ""}`}
              aria-hidden="true"
            ></i>
            {hasNotifications && (
              <span className="font-semibold text-chips-gold">
                {this.state.unreadNotifications}
              </span>
            )}
          </RLink>

          {/* Username dropdown */}
          <div className="relative flex items-center" ref={this.dropdownRef}>
            <button
              onClick={this.toggleUserDropdown}
              className="flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-normal transition hover:bg-slate-100 dark:hover:bg-white/10 cursor-pointer"
            >
              <div className="flex flex-col items-start gap-0.5">
                <div className="flex items-center gap-1.5">
                  <RLink
                    to={`/user/${user.username}`}
                    className="font-medium tracking-wide hover:underline dark:text-white transition-colors"
                    onClick={(e) => {
                      e.stopPropagation();
                    }}
                  >
                    {user.username}
                  </RLink>
                  <span className="text-xs font-light uppercase text-slate-500 dark:text-white/70">
                    (LV {user.level})
                  </span>
                </div>
                <div className="flex items-center gap-1 text-xs text-slate-500 dark:text-white/70">
                  {user.gold} <GoldCoin /> {user.silver} <SilverCoin />
                </div>
              </div>
              <i
                className={`fa fa-chevron-down text-xs transition-transform ${
                  this.state.userDropdownOpen ? "rotate-180" : ""
                }`}
                aria-hidden="true"
              ></i>
            </button>

            {this.state.userDropdownOpen && (
              <>
                <div className="absolute right-0 top-full z-50 mt-2 min-w-[180px] overflow-hidden rounded-lg border border-slate-200 bg-white shadow-lg dark:border-white/10 dark:bg-slate-800">
                  <RLink
                    white
                    noUnderline
                    to="/edit/profile"
                    className="flex w-full items-center gap-2 px-4 py-2 text-left text-sm hover:bg-slate-100 dark:hover:bg-white/10"
                    onClick={this.closeUserDropdown}
                  >
                    <PencilIcon className="h-4 w-4" />
                    Edit profile
                  </RLink>
                  <RLink
                    white
                    noUnderline
                    to={`/user/${user.username}`}
                    className="flex w-full items-center gap-2 px-4 py-2 text-left text-sm hover:bg-slate-100 dark:hover:bg-white/10"
                    onClick={this.closeUserDropdown}
                  >
                    <UserIcon className="h-4 w-4" />
                    View profile
                  </RLink>
                  {this.props.location.pathname === "/entry/3411" ? (
                    <MiyoEasterEggMenu onCloseDropdown={this.closeUserDropdown} />
                  ) : (
                    <>
                      <RLink
                        white
                        noUnderline
                        to="/explain-xp"
                        className="flex w-full items-center gap-2 px-4 py-2 text-left text-sm hover:bg-slate-100 dark:hover:bg-white/10"
                        onClick={this.closeUserDropdown}
                      >
                        <StarIcon className="h-4 w-4" />
                        Learn about XP
                      </RLink>
                      <RLink
                        white
                        noUnderline
                        to="/explain-gold"
                        className="flex w-full items-center gap-2 px-4 py-2 text-left text-sm hover:bg-slate-100 dark:hover:bg-white/10"
                        onClick={this.closeUserDropdown}
                      >
                        <span className="w-4">
                          <GoldCoin />
                        </span>
                        Learn about Gold
                      </RLink>
                      {user.id === 1 && (
                        <RLink
                          white
                          noUnderline
                          to="/admin/support"
                          className="flex w-full items-center gap-2 px-4 py-2 text-left text-sm hover:bg-slate-100 dark:hover:bg-white/10"
                          onClick={this.closeUserDropdown}
                        >
                          <WrenchScrewdriverIcon className="h-4 w-4" />
                          Support
                        </RLink>
                      )}
                      <div className="my-1 border-t border-slate-200 dark:border-white/10" />
                      <button
                        onClick={this.toggleTheme}
                        className="flex w-full items-center gap-2 px-4 py-2 text-left text-sm hover:bg-slate-100 dark:hover:bg-white/10"
                        data-testid={TestIds.ThemeToggleButton}
                      >
                        {this.state.theme === "dark" ? (
                          <SunIcon className="h-4 w-4" />
                        ) : (
                          <MoonIcon className="h-4 w-4" />
                        )}
                        {this.state.theme === "dark" ? "Light mode" : "Dark mode"}
                      </button>
                    </>
                  )}
                  <button
                    className="flex w-full items-center gap-2 px-4 py-2 text-left text-sm text-slate-900 hover:bg-slate-100 dark:text-white dark:hover:bg-white/10"
                    onClick={() => {
                      this.logout();
                      this.closeUserDropdown();
                    }}
                  >
                    <i className="fa fa-power-off w-4" aria-hidden="true"></i>
                    Log out
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      );
    } else {
      rightSide = (
        <div className="flex items-center gap-2 text-sm font-medium uppercase tracking-wide text-slate-700 dark:text-white">
          <RLink white to="/login">
            log in
          </RLink>
          <span className="text-slate-400 dark:text-white/30">|</span>
          <RLink white to="/register">
            sign up
          </RLink>
          <button
            onClick={this.toggleTheme}
            className="rounded-full border border-slate-200 p-2 text-xs uppercase tracking-wide text-slate-700 transition hover:bg-slate-100 dark:border-white/15 dark:text-white dark:hover:bg-white/10"
            aria-label="Toggle theme"
          >
            {this.state.theme === "dark" ? (
              <SunIcon className="h-4 w-4" />
            ) : (
              <MoonIcon className="h-4 w-4" />
            )}
          </button>
        </div>
      );
    }

    const topClass = this.props.isImpersonating ? "top-10" : "top-0";

    return (
      <div
        ref={(r) => {
          if (r) this.toolbar = r;
        }}
        className={`fixed inset-x-0 ${topClass} z-50 border-b border-slate-200 bg-white/80 text-slate-900 backdrop-blur dark:border-white/10 dark:bg-slate-900/95 dark:text-white`}
      >
        <div className="flex w-full items-center justify-between gap-2 px-3 py-2 md:gap-3 md:px-4">
          {/* Left side: Logo + Search */}
          <div className="flex flex-1 items-center gap-2 md:gap-3">
            <RLink
              white
              noUnderline
              to={isLoggedIn ? "/feed" : "/"}
              className="group flex shrink-0 items-center gap-2 text-lg font-medium tracking-wide text-slate-800 hover:text-slate-900 dark:text-white/90 dark:hover:text-white"
              onClick={(e) => {
                if (this.props.location.pathname === "/feed") {
                  e.preventDefault();
                  window.scrollTo({ top: 0, behavior: "smooth" });
                }
              }}
            >
              <i
                className="fa fa-play text-chips-gold/90 group-hover:text-chips-gold"
                aria-hidden="true"
              ></i>
              <span>chips</span>
            </RLink>

            <div className="max-w-lg flex-1">
              <Search />
            </div>
          </div>

          {/* Right side: User controls */}
          <div
            className="toolbar-right flex shrink-0 items-center"
            data-testid={TestIds.ToolbarUserSummary}
          >
            {rightSide}
          </div>
        </div>
      </div>
    );
  }
}

export type NavToolbarProps = RouteComponentProps<{}> & {
  notifications: API_UserNotifications | null;
};

export function NavToolbar(props: NavToolbarProps): JSX.Element {
  const userState = useUser();
  const isImpersonating = useIsImpersonating();
  // Don't render nav toolbar while loading user - avoid flash
  if (userState.type === "loading") {
    return <></>;
  }
  // Pass User or "logged-out" to the inner component
  const user = userState.type === "logged-in" ? userState.user : "logged-out";
  return <NavToolbarInner {...props} user={user} isImpersonating={isImpersonating} />;
}
